import { Create, Delete, Edit } from '@mui/icons-material'
import { Button, DialogActions, DialogContent, DialogTitle, IconButton, TableCell, Typography } from '@mui/material'
import { action, runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { useEffect, useMemo } from 'react'
import { useHistory } from 'react-router'
import { PortalVersion } from '../../../../../api/DTOtemp'
import { PortalsClient } from '../../../../../api/clients/identity/PortalsClient'
import { AssignmentType } from '../../../../../api/clients/workflowTasks/DTOs'
import UserChip from '../../../../../components/chips/UserChip'
import { FullscreenSpinner } from '../../../../../components/feedback/circular'
import { TableColumn } from '../../../../../components/tables/DTO'
import { FilteredTable } from '../../../../../components/tables/FilteredTable'
import { FilterControl, SelectFilterOption } from '../../../../../components/tables/filters/DTO'
import { usePrompts } from '../../../../../services/notifications/PromptService'
import { toastService } from '../../../../../services/notifications/ToastService'
import AdministrationPageContainer from '../../AdministrationPageContainer'
import UploadPortalDialog from './CreatePortalDialog'

export const PortalsPage = observer(() => {
	const portalApi = new PortalsClient()
	const history = useHistory()
	const prompts = usePrompts()

	const localStore = useLocalObservable(() => ({
		dialogOpen: false,
		portals: [] as PortalVersion[]
	}))

	const deletePortal = (portalId: number) => {
		portalApi
			.DeletePortal(portalId)
			.then(() => {
				// removes the deleted portal from the table
				runInAction(() => localStore.portals = (localStore.portals ?? []).filter((v) => v.portalId !== portalId))
				toastService.displayToast({
					message: 'Portal deleted',
					area: 'global',
					severity: 'success',
				})
			})
			.catch((v) => {
				console.error(v)
				toastService.displayToast({
					message: 'Error deleting portal',
					area: 'global',
					severity: 'error',
				})
			})
	}

	const handleDialogOpen = action(() =>
		localStore.dialogOpen = true
	)

	const handleDialogClose = action(() =>
		localStore.dialogOpen = false
	)

	// gets the latest version of each portal to display in the table
	useEffect(() => {
		const versionsPromise = portalApi.GetLatestPortalVersions()
		versionsPromise
			.then((v) => runInAction(() => localStore.portals = v.data))
			.catch((v) => console.error(v))
	}, [])

	const getUserOptions = useMemo((): SelectFilterOption[] => {
		const userOptions: Record<string, unknown> = {}
		localStore.portals?.forEach((p) => userOptions[p.lastModifiedBy] = true)
		return Object.keys(userOptions).map((k) => ({
			label: <UserChip
				assignmentType={AssignmentType.User}
				id={Number(k)}
			/>,
			value: Number(k)
		}))
	}, [localStore.portals])

	const filterControls: FilterControl[] = useMemo(() => [
		{
			type: 'date range',
			name: 'lastModifiedDate',
			label: 'Last Edited'
		},
		{
			type: 'multiselect',
			name: 'lastModifiedBy',
			label: "Edited By",
			options: getUserOptions
		},
		{
			type: 'search',
			name: 'search',
			column: ["name", "description"]
		}
	], [localStore.portals])

	const columns: TableColumn<PortalVersion>[] = useMemo(() => [
		{
			name: "name",
			label: "Portal Name"
		},
		{
			name: "description",
			label: "Portal Description"
		},
		{
			name: "lastModifiedDate",
			label: "Last Edited",
			renderContent: (p) => new Date(p.lastModifiedDate).toLocaleString()
		},
		{
			name: "lastModifiedBy",
			label: "Edited By",
			renderContent: (p) =>
				<UserChip
					assignmentType={AssignmentType.User}
					id={p.lastModifiedBy}
				/>
		},
		{
			renderHeading: <TableCell key={4} align="right">Actions</TableCell>,
			renderCell: (p) =>
				<TableCell key={4} align="right">
					<IconButton
						onClick={() => {
							history.push(`/_administration/_portals/${p.portalId}`)
						}}
					>
						<Edit fontSize="small" />
					</IconButton>
					<IconButton
						onClick={() => {
							prompts
								.showDialog((props) => (
									<ConfirmDeletePortalPrompt
										portalName={p.name}
										onClose={() => props.close('cancel')}
										onConfirm={() => props.close('okay')}
									/>
								))
								.then((r) => {
									if (r === 'okay') deletePortal(p.portalId)
								})
						}}
					>
						<Delete fontSize="small" />
					</IconButton>
				</TableCell>
		}
	], [])

	return (
		<AdministrationPageContainer
			title={'Portals'}
			actions={
				<div>
					<Button
						variant="contained"
						color="primary"
						startIcon={<Create />}
						onClick={handleDialogOpen}
					>
						Create Portal
					</Button>
				</div>
			}
		>
			{localStore.portals === undefined ? (
				<FullscreenSpinner />
			) : (
				<FilteredTable
					allEntries={localStore.portals}
					columns={columns}
					filterControls={filterControls}
				/>
			)}
			<UploadPortalDialog
				open={localStore.dialogOpen}
				onClose={handleDialogClose}
				onPortalUploaded={(newPortal) => {
					history.push(`_portals/${newPortal.portalId}`)
				}}
			/>
		</AdministrationPageContainer>
	)
})

type ConfirmDeletePortalPromptProps = {
	portalName: string
	onConfirm: () => void
	onClose: () => void
}

const ConfirmDeletePortalPrompt = (props: ConfirmDeletePortalPromptProps) => {
	return (
		<>
			<DialogTitle>Confirm Delete Portal</DialogTitle>
			<DialogContent>
				<Typography>
					Are you sure that you want to delete <b>{props.portalName}</b>?
				</Typography>
			</DialogContent>
			<DialogActions>
				<Button color="primary" onClick={props.onClose}>
					Cancel
				</Button>
				<Button variant="contained" color="primary" onClick={props.onConfirm}>
					Confirm
				</Button>
			</DialogActions>
		</>
	)
}